import { request } from './admin';

export const PERIOD_PRESETS = [
    'today',
    'yesterday',
    'last7d',
    'monthToDate',
    'previousMonth',
    'yearToDate',
    'custom',
] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

export const PLAN_TYPES = ['monthly', 'yearly', 'free'] as const;
export type PlanType = (typeof PLAN_TYPES)[number];

export const SUBSCRIPTION_STATUSES = [
    'ativa',
    'em_atraso',
    'gratuita',
    'suspensa',
    'cancelada',
    'reembolsada',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/** Filtros globais do painel. Campos vazios nao entram na query. */
export interface AnalyticsFilters {
    period: PeriodPreset;
    from?: string;
    to?: string;
    planType?: PlanType;
    status?: SubscriptionStatus;
    segmento?: string;
    faturamento?: string;
}

export interface AnalyticsOption {
    value: string;
    label: string;
}

/** Evento que a tabela Eventos por cliente pode acompanhar (login, lancamento, analise, features). */
export interface AnalyticsEvent {
    id: string;
    label: string;
}

export interface AnalyticsMeta {
    generatedAt: string;
    timezone: string;
    events?: AnalyticsEvent[];
    /** Desde quando cada tipo de dado existe (dia local AAAA-MM-DD ou instante ISO). */
    coverage?: {
        /** Inicio do historico de acesso; funil, D7 e TTV so contam contas cadastradas a partir dele. */
        acesso: string | null;
    };
    filters: {
        segmentos: AnalyticsOption[];
        faturamentos: AnalyticsOption[];
    };
}

/** Valor do periodo contra o periodo anterior de mesma duracao. Sem base, nulo. */
export interface AnalyticsCard {
    value: number | null;
    previous: number | null;
    change: number | null;
    changePct: number | null;
}

export type TrendGranularity = 'day' | 'week' | 'month';

export interface RevenueBaseTrendPoint {
    label: string;
    from: string;
    to: string;
    mrr: number;
    arr: number;
    pagantesAtivas: number;
    contasComAcesso: number;
    contasTotais: number;
    crescimentoPagantesPct: number | null;
    variacaoPagantes: number;
}

export interface AcquisitionChurnTrendPoint {
    label: string;
    from: string;
    to: string;
    novasContas: number;
    cancelamentos: number;
    churnPct: number | null;
}

/** Contagem sobre uma base. Sem base (base 0), o percentual vem nulo. */
export interface PctCount {
    count: number;
    base: number;
    pct: number | null;
}

export interface PlanShare {
    count: number;
    pct: number | null;
    previous: number;
}

export interface PlanosAtivacao {
    gratuitasTrial: PlanShare;
    mensais: PlanShare;
    anuais: PlanShare;
    tempoMedioPrimeiroLancamentoDias: number | null;
    ativacaoD7: PctCount;
    ttvMedianoDias: number | null;
}

export const FUNNEL_STAGE_IDS = [
    'cadastrou',
    'primeiro_login',
    'pesquisa',
    'primeiro_lancamento',
    'retornou_d7',
    'crfa',
] as const;
export type FunnelStageId = (typeof FUNNEL_STAGE_IDS)[number];

export interface FunnelStage {
    id: FunnelStageId;
    count: number;
    pct: number | null;
}

export interface ActivationFunnel {
    /** Empresas cadastradas no periodo com sete dias completos de observacao. */
    base: number;
    stages: FunnelStage[];
}

export interface AnalyticsOverview {
    period: {
        preset: PeriodPreset;
        from: string;
        to: string;
        previousFrom: string;
        previousTo: string;
    };
    /** Filtro de status ligado: as taxas de churn vem nulas de proposito. */
    filtroDeStatus: boolean;
    cards: {
        contasTotais: AnalyticsCard;
        contasComAcesso: AnalyticsCard & { pct: number | null };
        pagantesAtivas: AnalyticsCard & { pct: number | null };
        mrr: AnalyticsCard;
        arr: AnalyticsCard;
        /** `count` pagantes do inicio que sairam, sobre `base` pagantes do inicio. */
        churn: AnalyticsCard & { count: number; base: number };
        crescimentoPagantes: AnalyticsCard & { diff: number; previousDiff: number };
        novasContas: AnalyticsCard;
        cancelamentos: AnalyticsCard;
        mrrCancelado: AnalyticsCard;
    };
    trends: {
        granularity: TrendGranularity;
        receitaBase: RevenueBaseTrendPoint[];
        aquisicaoChurn: AcquisitionChurnTrendPoint[];
    };
    planosAtivacao: PlanosAtivacao;
    funil: ActivationFunnel;
    setupD30: PctCount;
    pagouNuncaAcessou: { count: number };
}

/** Recorte de uso: cada percentual usa como base as contas do proprio recorte. */
export interface UsageBlock {
    base: number;
    dau: PctCount;
    wau: PctCount;
    mau: PctCount;
    ausentes: PctCount;
    nuncaAcessou: PctCount;
}

export interface UsageSnapshot {
    day: string;
    /** Todas as contas do escopo, com ou sem acesso: base do "% do total" dos recortes. */
    totalContas: number;
    crfa: PctCount;
    geral: UsageBlock;
    pagantes: UsageBlock;
    gratuitasTrial: UsageBlock;
    usuariosUnicosDia: number;
    loginsDia: number;
    loginsPorConta30d: number | null;
    sessoesPorContaMes: number | null;
}

export interface UsageTrendPoint {
    label: string;
    to: string;
    crfa: PctCount;
    dau: PctCount;
    wau: PctCount;
    mau: PctCount;
    ausentes: PctCount;
    loginsPorConta30d: number | null;
}

export interface AnalyticsUsage {
    period: { preset: PeriodPreset; from: string; to: string };
    current: UsageSnapshot;
    previous: UsageSnapshot;
    trend: { granularity: TrendGranularity; points: UsageTrendPoint[] };
}

export interface RetentionCell {
    offset: number;
    month: string;
    count: number;
    /** Nulo quando o mes ainda esta em andamento ou a safra esta vazia. */
    pct: number | null;
    open: boolean;
}

export interface RetentionRow {
    month: string;
    size: number;
    cols: RetentionCell[];
}

export interface AnalyticsRetention {
    months: string[];
    rows: RetentionRow[];
}

/** Coortes mensais do evento escolhido: mesma forma da retencao geral. */
export interface AnalyticsCohorts {
    event: string;
    months: string[];
    rows: RetentionRow[];
}

export interface FeaturesCards {
    movimentacoes: AnalyticsCard;
    entradas: AnalyticsCard & { registros: number; previousRegistros: number };
    saidas: AnalyticsCard & { registros: number; previousRegistros: number };
    resultado: AnalyticsCard;
}

export interface FeatureTrendPoint {
    label: string;
    from: string;
    to: string;
    acoesDoEvento: number;
    contasQueUsaram: number;
    lancamentos: number;
}

export interface FeatureFlagItem {
    key: string;
    label: string;
    enabled: number;
    pct: number | null;
    enabledPayers: number;
}

export interface FeatureUsage {
    id: string;
    label: string;
    contas: number;
    acoes: number;
    pagantes: number;
    gratuitas: number;
    adocaoPagantes: number | null;
    adocaoGratuitas: number | null;
}

export interface AnalyticsFeatures {
    period: { preset: PeriodPreset; from: string; to: string };
    cards: FeaturesCards;
    trend: { granularity: TrendGranularity; event: string; metric: string; points: FeatureTrendPoint[] };
    featureFlags: { base: number; items: FeatureFlagItem[] };
    maisUtilizadas: FeatureUsage[];
    eventos: AnalyticsEvent[];
}

export interface AnalyticsTableColumn {
    key: string;
    label: string;
}

/** Tabela do painel: colunas na ordem de exibicao e linhas com valores crus do back-end. */
export interface AnalyticsTable {
    name: string;
    columns: AnalyticsTableColumn[];
    rows: Array<Record<string, unknown>>;
}

// ---------------------------------------------------------------------------
// Assinaturas (Geral, Receitas, Churn) e Mapeamento de clientes
// ---------------------------------------------------------------------------

interface PeriodRange {
    preset: PeriodPreset;
    from: string;
    to: string;
}

/** Cards de Assinaturas > Geral que filtram a tabela "Assinaturas por cliente". */
export const SUBSCRIPTION_CARDS = [
    'ativos',
    'gratuitas',
    'mensais',
    'anuais',
    'em_atraso',
    'recebido',
    'com_pagamento',
    'canceladas',
    'canceladas_mes',
    'convertidas',
] as const;
export type SubscriptionCardId = (typeof SUBSCRIPTION_CARDS)[number];

export interface SubscriptionBaseTrendPoint {
    label: string;
    to: string;
    pagantesAtivas: number;
    contasComAcesso: number;
    contasTotais: number;
}

export interface CountShare {
    count: number;
    pct: number | null;
}

export interface AnalyticsSubscriptions {
    period: PeriodRange;
    cards: {
        assinantesAtivos: AnalyticsCard & { pct: number | null };
        gratuitas: AnalyticsCard;
        mensais: AnalyticsCard;
        anuais: AnalyticsCard;
        emAtraso: AnalyticsCard & { mrr: number; previousMrr: number };
    };
    evolucao: { granularity: TrendGranularity; points: SubscriptionBaseTrendPoint[] };
    composicao: {
        total: number;
        ativas: CountShare;
        emAtraso: CountShare;
        gratuitas: CountShare;
        outras: CountShare;
    };
}

export interface RevenueTrendPoint {
    label: string;
    to: string;
    mrr: number;
    arr: number;
}

export interface AnalyticsRevenue {
    period: PeriodRange;
    cards: {
        mrr: AnalyticsCard;
        arr: AnalyticsCard;
        arpuMensal: AnalyticsCard;
        recebidoNoPeriodo: AnalyticsCard;
        /** `contas`: empresas com algum pagamento confirmado ate a data final. */
        ltvMedioPago: AnalyticsCard & { contas: number };
    };
    evolucao: { granularity: TrendGranularity; points: RevenueTrendPoint[] };
}

export interface ChurnTrendPoint {
    label: string;
    to: string;
    cancelamentos: number;
    churnPorContaPct: number | null;
}

export interface AnalyticsChurn {
    period: PeriodRange;
    /** Filtro de status ligado: as taxas de churn vem nulas de proposito. */
    filtroDeStatus: boolean;
    cards: {
        cancelamentos: AnalyticsCard;
        /** `count` pagantes do inicio que sairam, sobre `base` pagantes do inicio. */
        churnNoPeriodo: AnalyticsCard & { count: number; base: number };
        churnMensal: { value: number | null; month: string; count: number; base: number };
        churnAnual: { value: number | null };
        mrrCancelado: AnalyticsCard;
        churnDeMrr: AnalyticsCard;
        receitaPerdidaReal: { value: number | null };
        trialAtePrimeiroPagamentoDias: { value: number | null };
        ateCancelamentoDias: { value: number | null };
    };
    evolucao: { granularity: TrendGranularity; points: ChurnTrendPoint[] };
}

export interface DistributionItem {
    value: string;
    label: string;
    count: number;
    pct: number | null;
}

export interface SectorBehavior {
    setor: string;
    respondentes: number;
    pagantes: number | null;
    crfaNoGrupo: number | null;
    ausentes: number | null;
    contagem: { pagantes: number; crfaNoGrupo: number; ausentes: number };
}

export interface ControlActivation {
    controle: string;
    label: string;
    respondentes: number;
    pagantes: number | null;
    ativadas: number | null;
    crfaNoGrupo: number | null;
    contagem: { pagantes: number; ativadas: number; crfaNoGrupo: number };
}

export interface AnalyticsMapping {
    period: PeriodRange;
    cards: {
        contasCriadas: number;
        responderam: PctCount;
        semResposta: number;
        setorMaisFrequente: string | null;
        controleMaisComum: string | null;
    };
    setorAgrupado: Array<{ setor: string; count: number; pct: number | null }>;
    comoControla: DistributionItem[];
    segmento: DistributionItem[];
    faturamentoAnual: DistributionItem[];
    numeroFuncionarios: DistributionItem[];
    objetivos: DistributionItem[];
    comoConheceu: DistributionItem[];
    setorXComportamento: SectorBehavior[];
    controleXAtivacao: ControlActivation[];
}

export type AnalyticsTableName =
    | 'funil'
    | 'setup-d30'
    | 'pagou-nunca-acessou'
    | 'sem-atividade'
    | 'eventos-por-cliente'
    | 'movimentacoes-por-cliente'
    | 'funcionalidades-mais-utilizadas'
    | 'uso-por-cliente'
    | 'assinaturas-por-cliente'
    | 'respostas-por-cliente';
export type TableParams = Record<string, string | undefined>;

export function buildAnalyticsQuery(filters: AnalyticsFilters): string {
    const q = new URLSearchParams();
    q.set('period', filters.period);
    if (filters.period === 'custom') {
        if (filters.from) q.set('from', filters.from);
        if (filters.to) q.set('to', filters.to);
    }
    if (filters.planType) q.set('planType', filters.planType);
    if (filters.status) q.set('status', filters.status);
    if (filters.segmento) q.set('segmento', filters.segmento);
    if (filters.faturamento) q.set('faturamento', filters.faturamento);
    return q.toString();
}

/** Filtros globais mais os parametros proprios da tabela (ex.: stage do funil). */
export function buildTableQuery(filters: AnalyticsFilters, params: TableParams = {}): string {
    const q = new URLSearchParams(buildAnalyticsQuery(filters));
    for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
    return q.toString();
}

export const analyticsApi = {
    meta: (signal?: AbortSignal): Promise<AnalyticsMeta> =>
        request('/admin/analytics/meta', { signal }),

    overview: (filters: AnalyticsFilters, signal?: AbortSignal): Promise<AnalyticsOverview> =>
        request(`/admin/analytics/overview?${buildAnalyticsQuery(filters)}`, { signal }),

    usage: (filters: AnalyticsFilters, signal?: AbortSignal): Promise<AnalyticsUsage> =>
        request(`/admin/analytics/usage?${buildAnalyticsQuery(filters)}`, { signal }),

    retention: (filters: AnalyticsFilters, signal?: AbortSignal): Promise<AnalyticsRetention> =>
        request(`/admin/analytics/usage/retention?${buildAnalyticsQuery(filters)}`, { signal }),

    cohorts: (filters: AnalyticsFilters, event: string, signal?: AbortSignal): Promise<AnalyticsCohorts> =>
        request(`/admin/analytics/usage/cohorts?${buildTableQuery(filters, { event })}`, { signal }),

    features: (filters: AnalyticsFilters, event: string, signal?: AbortSignal): Promise<AnalyticsFeatures> =>
        request(`/admin/analytics/features?${buildTableQuery(filters, { event })}`, { signal }),

    subscriptions: (filters: AnalyticsFilters, signal?: AbortSignal): Promise<AnalyticsSubscriptions> =>
        request(`/admin/analytics/business/subscriptions?${buildAnalyticsQuery(filters)}`, { signal }),

    revenue: (filters: AnalyticsFilters, signal?: AbortSignal): Promise<AnalyticsRevenue> =>
        request(`/admin/analytics/business/revenue?${buildAnalyticsQuery(filters)}`, { signal }),

    churn: (filters: AnalyticsFilters, signal?: AbortSignal): Promise<AnalyticsChurn> =>
        request(`/admin/analytics/business/churn?${buildAnalyticsQuery(filters)}`, { signal }),

    mapping: (filters: AnalyticsFilters, signal?: AbortSignal): Promise<AnalyticsMapping> =>
        request(`/admin/analytics/business/mapping?${buildAnalyticsQuery(filters)}`, { signal }),

    table: (
        name: AnalyticsTableName,
        filters: AnalyticsFilters,
        params?: TableParams,
        signal?: AbortSignal,
    ): Promise<AnalyticsTable> =>
        request(`/admin/analytics/tables/${name}?${buildTableQuery(filters, params)}`, { signal }),

};
